/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import FeeSearch from "./components/FeeSearch";
import FeeList from "./components/FeeList";
import { Box } from "@chakra-ui/react";
import moment from "moment";
import Card from "components/card";
import DisplayAmount from "../../../views/admin/shared/DisplayAmount";
import { CSSTransition } from "react-transition-group";
import FeeToolbar from "./components/FeeToolbar";

type Asset = {
  asset: string;
  available: string;
  pending: string;
};
interface Filters {
  [key: string]: any;
}

interface PayloadType {
  [key: string]: any;
}

const rowPerPage = 10;

const initFrom = moment().subtract(1, "month");
const initTo = moment();
const initialDateRangeFilter = [initFrom.toDate(), initTo.toDate()];

const initialFilters: Filters = {
  from: initFrom,
  to: initTo,
  provider_account_id: "",
};

const Fee = ({ userPermissions }: { userPermissions: any }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchReportSubmitted, setIsFetchReportSubmitted] =
    useState<boolean>(false);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [selectedDates, setSelectedDates] = useState<Date[]>(
    initialDateRangeFilter
  );
  const [balanceSummary, setBalanceSummary] = useState<any>(null);
  const nodeReportRef = useRef(null);

  // NEW: control filter toggle
  const [showFilters, setShowFilters] = useState(true);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    localStorage.removeItem("providerIdToFilterTransaction");
    localStorage.removeItem("providerNameToFilterTransaction");
    localStorage.removeItem("providerAccountIdToFilterTransaction");
    localStorage.removeItem("providerAccountNameToFilterTransaction");
    setIsFilterLoading(false);
  }, []);

  const fetchFees = async (payload: PayloadType = {}, isReset = false, signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const formattedFromDate = moment(
        !isReset ? selectedDates[0] : initFrom,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 00:00");
      const formattedToDate = moment(
        !isReset ? selectedDates[1] : initTo,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 23:59");

      const providerAccountId = localStorage.getItem(
        "providerAccountIdToFilterTransaction"
      );

      const queryObject: PayloadType = {
        limit: payload?.limit,
        page: payload?.page + 1,
        from: formattedFromDate,
        to: formattedToDate,
        provider_account_id: providerAccountId,
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/transaction/get-all-transactions?${queryString}`,
        { signal }
      );

      let newData = response.data.transactions;
      let totalTransactions = response.data.totalTransactions;

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      await getSubaccountBalances(providerAccountId, signal);

      setIsLoading(false);
      setIsTableLoading(false);
      setRowCount(newData && newData.length !== 0 ? totalTransactions : 0);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      setTableData(null);
      setIsLoading(false);
      setIsTableLoading(false);
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsTableLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      provider_account_id: localStorage.getItem(
        "providerAccountIdToFilterTransaction"
      ),
      ...filters,
    };

    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        provider_account_id: localStorage.getItem(
          "providerAccountIdToFilterTransaction"
        ),
        ...filters,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
    } else {
      setPage(newValue.page);
    }

    fetchFees(newFilters);
  };

  const getSubaccountBalances = async (providerAccountId: string, signal?: AbortSignal) => {
    try {
      const formattedFromDate = moment(selectedDates[0], "DD/MM/YYYY").format(
        "YYYY-MM-DD 00:00"
      );
      const formattedToDate = moment(selectedDates[1], "DD/MM/YYYY").format(
        "YYYY-MM-DD 23:59"
      );

      const queryObject: PayloadType = {
        type: "provider",
        id: providerAccountId,
        from: formattedFromDate,
        to: formattedToDate,
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-balance?${queryString}`,
        { signal }
      );

      const balances = response.data?.balances;
      if (balances) {
        setBalanceSummary(balances);
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error get balance data", error);
      setIsLoading(false);
      return false;
    }
  };

  const handleSearchSubmit = async () => {
    setIsFetchReportSubmitted(true);
    setTableData(null);
    setBalanceSummary(null);
    setPage(0);
    await fetchFees({ page: 0, limit: rowPerPage, ...filters });
  };

  const handleSearchClear = async () => {
    setIsFetchReportSubmitted(false);
    setTableData(null);
    localStorage.removeItem("providerIdToFilterTransaction");
    localStorage.removeItem("providerAccountIdToFilterTransaction");
    setFilters(initialFilters);
    setPage(0);
    setSelectedDates(initialDateRangeFilter);
  };

  const countNonZeroAvailable = (data: Asset[]): Asset[] => {
    return data.filter((item) => parseFloat(item.available) > 0);
  };

  const triggerReloadFeeList = async () => {
    setIsLoading(true);
    await fetchFees({
      page: page,
      limit: rowPerPage,
      ...filters,
    });
  };

  return (
    <Box sx={{ position: "relative" }}>
      <FeeToolbar
        isLoading={isLoading}
        onToggleFilters={() => setShowFilters((p) => !p)}
        onRefresh={triggerReloadFeeList}
      />

      <FeeSearch
        showFilters={showFilters}
        tableData={tableData}
        userPermissions={userPermissions}
        isLoading={isLoading}
        isFilterLoading={isFilterLoading}
        filters={filters}
        setFilters={setFilters}
        selectedDates={selectedDates}
        setSelectedDates={setSelectedDates}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
      />
      <CSSTransition
        in={
          isFetchReportSubmitted &&
          balanceSummary !== null &&
          tableData !== null
        }
        timeout={300}
        classNames="slide-down"
        unmountOnExit
        nodeRef={nodeReportRef}
      >
        <div ref={nodeReportRef}>
          {isFetchReportSubmitted && balanceSummary && tableData && (
            <>
              <Card className="card !shadow-xs mt-3 !rounded-xl p-6">
                {/* Header */}
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                    Total Cost
                  </h3>
                </div>

                {/* Summary */}
                {countNonZeroAvailable(balanceSummary).length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-6">
                    {countNonZeroAvailable(balanceSummary).map(
                      (item: any, index: number) => (
                        <div
                          key={item.asset}
                          className={`min-w-[120px] flex-1 md:flex-none ${
                            index !== 0 ? "border-l border-gray-200 pl-6" : ""
                          }`}
                        >
                          <div className="text-sm font-medium text-navy-700">
                            {item.asset}
                          </div>
                          <div className="mt-1 text-2xl font-bold text-primary-500">
                            {item.crypto ? (
                              item.available
                            ) : (
                              <DisplayAmount
                                amount={Number(item.available)}
                                currency={item.asset}
                              />
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div className="mt-4 text-sm text-navy-600 dark:text-white">
                    No expenses recorded
                  </div>
                )}
              </Card>
              <FeeList
                isLoading={isLoading}
                isTableLoading={isTableLoading}
                page={page}
                pageSize={pageSize}
                rowCount={rowCount}
                tableData={isLoading ? [] : tableData}
                setPaginationModel={setPaginationModel}
              />
            </>
          )}
        </div>
      </CSSTransition>
    </Box>
  );
};

export default Fee;
